import type { IDraftProductionStatement } from "@/interfaces/api/production-statement.interface"
import { Project } from "@/models/project.model"
import { ProductionStatement } from "@/models/production-statement.model"
import ModelManager from "@/services/model-manager"

/**
 * An unsaved production-statement preview. Drafts must stay unmanaged: their
 * request identity is kept in the same project and period fields as saved
 * statements. `draftKey` is derived for client-side rendering and local detail
 * caching only.
 *
 * The drafts endpoint returns drafts as shallow models: they only carry the
 * annotated totals and the ids of the items they cover. A draft is expanded
 * client-side by retrieving those items and then promoting the draft, see
 * `promote()`.
 */
export class DraftProductionStatement extends ProductionStatement {
  public static override className = "DraftProductionStatement"

  declare id: undefined
  isSaved = false
  isManaged = false
  isComplete = false

  constructor(draft: IDraftProductionStatement) {
    super(draft, true)
    this.id = undefined

    this.isComplete = draft.isComplete

    this.whenResolved = this.resolveProject()
  }

  /**
   * Drafts are intentionally unmanaged, but their API response only contains
   * `projectId`. Link that id to the managed project cache without registering
   * the draft itself in ModelManager.
   */
  public resolveProject(): Promise<void> {
    return new Promise((resolve) => {
      const project = ModelManager.getInstance().get(
        this.project as unknown as string,
        (resolvedProject) => {
          this.project = resolvedProject as Project
          resolve()
        }
      )

      if (project) {
        this.project = project as Project
        resolve()
      }
    })
  }

  public override isDraft(): this is DraftProductionStatement {
    return true
  }

  public override getDisplayText(): string {
    return this.project?.name
      ? `productieverantwoording voor project ${this.project.name}`
      : "productieverantwoording"
  }

  /**
   * A copy of this draft that is no longer shallow, to be used once the items it
   * references have been retrieved.
   *
   * Promoting swaps the shallow marker of the drafts endpoint for a client-side one, so
   * `isShallow()` reports false: the detail UI renders and the item computeds resolve
   * the referenced items from the stores instead of falling back on the annotations.
   */
  public promote(): DraftProductionStatement {
    this.shallow = false
    return this
  }

  get draftKey(): string {
    return [
      this.project.id,
      this.periodStart?.format("YYYY-MM-DD") ?? "",
      this.periodEnd?.format("YYYY-MM-DD") ?? ""
    ].join(":")
  }
}
